import React, { useEffect, useState } from 'react';
import { Zap, Truck, MapPin, Clock, Loader2, Navigation } from 'lucide-react';
import api from '../../services/api';
import useBuyerLocation from '../../hooks/useBuyerLocation';

const fmtDate = (iso, opts) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es-MX', { timeZone: 'UTC', ...opts });
};

export const nationalEtaText = (eta) => {
  if (eta.min_date === eta.max_date) return `Llega el ${fmtDate(eta.min_date, { weekday: 'long', day: 'numeric', month: 'long' })}`;
  return `Llega entre el ${fmtDate(eta.min_date, { weekday: 'long', day: 'numeric' })} y el ${fmtDate(eta.max_date, { weekday: 'long', day: 'numeric', month: 'long' })}`;
};

const LocalBadge = ({ eta }) => {
  const at = new Date(eta.arrives_at).toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit' });
  return (
    <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500 via-green-500 to-lime-400 p-4 text-white shadow-lg shadow-emerald-500/30" data-testid="delivery-badge-local">
      <div className="absolute inset-0 bg-[linear-gradient(110deg,transparent_35%,rgba(255,255,255,0.35)_50%,transparent_65%)] bg-[length:250%_100%] animate-[shine_2.8s_linear_infinite]" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center shrink-0"><Zap className="w-5 h-5 fill-white" /></div>
        <div>
          <p className="font-extrabold leading-tight">Lo que ves, está a minutos de tu puerta ⚡</p>
          <p className="text-sm text-white/95 mt-0.5" data-testid="delivery-local-eta">Llega hoy en ~{eta.minutes} min · aprox. {at}</p>
        </div>
      </div>
    </div>
  );
};

const FreeShippingBadge = () => (
  <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#0040FF] via-[#0066FF] to-[#00B2FF] p-4 text-white shadow-lg shadow-blue-600/40" data-testid="delivery-badge-free">
    <div className="absolute inset-0 bg-[linear-gradient(110deg,transparent_35%,rgba(255,255,255,0.3)_50%,transparent_65%)] bg-[length:250%_100%] animate-[shine_3.2s_linear_infinite]" />
    <div className="relative flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0"><Truck className="w-5 h-5" /></div>
      <p className="font-extrabold text-lg leading-tight">Envío Gratis a todo México 🚚</p>
    </div>
  </div>
);

const EtaLine = ({ data, status, onRequest }) => {
  const eta = data?.national_eta;
  if (eta) {
    return (
      <div className="flex items-start gap-2 text-sm" data-testid="delivery-national-eta">
        <Clock className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
        <div>
          <p className="font-semibold text-green-700 first-letter:uppercase">{nationalEtaText(eta)}</p>
          <p className="text-xs text-gray-500">De {eta.origin_state} ({eta.origin_region}) a {eta.dest_state} ({eta.dest_region}) · {eta.label}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700" data-testid="delivery-generic-eta">
      <Clock className="w-4 h-4 text-gray-500" />
      <span>Llega entre 48h y 5 días dependiendo tu región</span>
      {status !== 'asking' && status !== 'unavailable' && (
        <button onClick={onRequest} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline" data-testid="delivery-use-location-btn">
          <Navigation className="w-3 h-3" /> Usar mi ubicación
        </button>
      )}
    </div>
  );
};

export const DeliveryPromise = ({ product }) => {
  const { location, status, request } = useBuyerLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const sellerId = product?.seller_id;

  useEffect(() => {
    if (!sellerId || status === 'asking') return;
    let alive = true;
    setLoading(true);
    const params = location ? { lat: location.lat, lng: location.lng } : {};
    api.get(`/logistics/predictive-availability/${sellerId}`, { params })
      .then(r => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [sellerId, location, status]);

  const freeShipping = product.free_shipping || product.freeShipping;

  if (loading && !data) {
    return <div className="flex items-center gap-2 text-sm text-gray-500 mb-4" data-testid="delivery-loading"><Loader2 className="w-4 h-4 animate-spin" /> Calculando tu entrega…</div>;
  }

  return (
    <div className="space-y-3 mb-4" data-testid="delivery-promise">
      <style>{'@keyframes shine{from{background-position:200% 0}to{background-position:-50% 0}}'}</style>
      {data?.hasLocalDriver ? <LocalBadge eta={data.local_eta} /> : freeShipping ? <FreeShippingBadge /> : null}
      {!data?.hasLocalDriver && <EtaLine data={data} status={status} onRequest={request} />}
      {data?.location_source && data.location_source !== 'gps' && data.dest_state && (
        <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Calculado con tu dirección en {data.dest_state}</p>
      )}
    </div>
  );
};
